import { useMemo, useState } from "react";
import {
  Table,
  Button,
  Space,
  Modal,
  Form,
  Input,
  InputNumber,
  Select,
  Popconfirm,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import {
  PlusOutlined,
  DeleteOutlined,
  EditOutlined,
  SearchOutlined,
  PictureOutlined,
  FileExcelOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
  EnvironmentOutlined,
} from "@ant-design/icons";
import Layout from "../../Components/Layout";
import PageHeader from "../../Components/PageHeader";
import { useSidebar } from "../../hooks/useSidebar";
import { useCachedList } from "../../hooks/useCachedList";
import { useFiltroPersistido } from "../../hooks/useFiltroPersistido";
import AttachmentsManager from "../../Components/AttachmentsManager";
import { CatalogSelect } from "../../Components/CatalogTags";
import GeoSelect, { type GeoValue } from "../../Components/GeoSelect";
import ZonaCell from "../../Components/ZonaCell";
import {
  costosSugeridosApi,
  type CostoSugerido,
  type CostoSugeridoCreate,
} from "../../services/costosSugeridos.api";
import { catalogosApi, type CatalogoTag } from "../../services/catalogos.api";
import { exportToXlsxSingleSheet } from "../finanzas-empresa/exportXls";
import { tagColor } from "../../utils/tagColors";
import { clpInputFormatter, clpInputParser } from "../../utils/clpInput";
import { getMsg } from "../../utils/antdStatic";

import { paginacionCliente } from "../../utils/tablaPaginacion";
const { Text, Paragraph } = Typography;

/** Detiene la propagación del click (celdas interactivas no expanden la fila). */
const stopClick = (e: React.MouseEvent) => e.stopPropagation();

/**
 * Página /costos-sugeridos — catálogo de costos referenciales de trabajos.
 *
 * Tabla-guía (admin/superadmin) para saber cuánto sale cada trabajo típico.
 * Round 2026-06-30: categoría desde el catálogo gestionable (crear/editar/borrar),
 * geografía opcional (región/comuna/sector), filas expandibles, filtro por
 * categoría, export a Excel y reorden manual. Es referencia interna, NO finanzas.
 *
 * Ver docs/modulo-costos-sugeridos.md.
 */

const money = (v?: number | null): string =>
  v === null || v === undefined
    ? "—"
    : new Intl.NumberFormat("es-CL", {
        style: "currency",
        currency: "CLP",
        maximumFractionDigits: 0,
      }).format(v);

/** Texto compacto de la zona (región › comuna › sector). */
function zonaText(c: CostoSugerido): string {
  return [c.regionNombre, c.comunaNombre, c.sectorNombre].filter(Boolean).join(" › ");
}

export default function CostosSugeridosPage() {
  const { sidebarOpen, toggleSidebar } = useSidebar();
  // Búsqueda y categorías se recuerdan por usuario admin en este navegador
  // (round 2026-09-24, ver docs/filtros-persistentes.md). Con cualquiera de los
  // dos puesto, `canReorder` deshabilita Subir/Bajar; el tooltip lo explica.
  const [search, setSearch] = useFiltroPersistido("costosSugeridos.busqueda", "", {
    validar: (v) => typeof v === "string",
  });
  const [filterCats, setFilterCats] = useFiltroPersistido<string[]>(
    "costosSugeridos.categorias",
    [],
    { validar: (v) => Array.isArray(v) && v.every((x) => typeof x === "string") },
  );
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<CostoSugerido | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [form] = Form.useForm<CostoSugeridoCreate>();
  const [categoriaVal, setCategoriaVal] = useState<string | null>(null);
  const [geoVal, setGeoVal] = useState<GeoValue>({ region: null, comuna: null, sector: null });

  // PERF (round 2026-07-02): stale-while-revalidate. La lista y el catálogo
  // se pintan AL INSTANTE desde sessionStorage (última visita) y se revalidan
  // en segundo plano al montar; el spinner solo aparece en la primera visita.
  // `refresh` es drop-in del viejo fetchList (las mutaciones lo llaman igual)
  // y `setData` cubre el update optimista del reorden. Ver hooks/useCachedList.
  const {
    data,
    loading,
    refresh: fetchList,
    setData,
  } = useCachedList<CostoSugerido>(
    "cache:costos-sugeridos:v1",
    async () => (await costosSugeridosApi.list())?.data ?? null,
  );
  const { data: catTags, refresh: fetchCatTags } = useCachedList<CatalogoTag>(
    "cache:catalogo:categoria_costo:v1",
    async () => (await catalogosApi.list("categoria_costo"))?.data ?? null,
  );

  const colorByCat = useMemo(() => {
    const m = new Map<string, string | null | undefined>();
    for (const t of catTags) m.set(t.nombre, t.color);
    return m;
  }, [catTags]);

  const canReorder = !search.trim() && filterCats.length === 0;

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return data.filter((c) => {
      if (filterCats.length && !(c.categoria && filterCats.includes(c.categoria))) return false;
      if (!q) return true;
      const hay = [c.nombre_trabajo, c.categoria, c.descripcion, zonaText(c)]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return hay.includes(q);
    });
  }, [data, search, filterCats]);

  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    setCategoriaVal(null);
    setGeoVal({ region: null, comuna: null, sector: null });
    setModalOpen(true);
  };

  const openEdit = (c: CostoSugerido) => {
    setEditing(c);
    form.setFieldsValue({
      nombre_trabajo: c.nombre_trabajo,
      precio: c.precio ?? null,
      descripcion: c.descripcion ?? "",
    });
    setCategoriaVal(c.categoria ?? null);
    setGeoVal({ region: c.region ?? null, comuna: c.comuna ?? null, sector: c.sector ?? null });
    setModalOpen(true);
  };

  const closeModal = () => {
    if (submitting) return;
    setModalOpen(false);
    setEditing(null);
    form.resetFields();
    void fetchList();
    void fetchCatTags(); // refresca chips/filtro por si se gestionaron categorías
  };

  const submit = async () => {
    if (submitting) return;
    try {
      const values = await form.validateFields();
      setSubmitting(true);
      const payload: CostoSugeridoCreate = {
        nombre_trabajo: (values.nombre_trabajo ?? "").trim(),
        categoria: categoriaVal || null,
        precio:
          values.precio === undefined || values.precio === null ? null : Number(values.precio),
        descripcion: (values.descripcion ?? "").trim() || null,
        region: geoVal.region,
        comuna: geoVal.comuna,
        sector: geoVal.sector,
      };
      // Guardar y CERRAR (pedido del dueño 2026-07-02): antes el modal quedaba
      // abierto tras crear (pasaba a modo edición para adjuntar fotos), lo que
      // confundía sobre si se guardó o no. Ahora cierra en éxito; para adjuntar
      // fotos de guía se reabre con Editar. Si el guardado falla (apiFetch
      // devuelve null + toast de error), el modal queda abierto con lo tipeado.
      // No usamos closeModal() aquí porque su guard `if (submitting) return`
      // lo anularía (submitting sigue true hasta el finally).
      const res = editing
        ? await costosSugeridosApi.update(editing.id, payload)
        : await costosSugeridosApi.create(payload);
      if (res?.data) {
        if (!editing) {
          getMsg()?.success(
            "Costo creado. Para adjuntar fotos de guía, ábrelo con Editar.",
          );
        }
        setModalOpen(false);
        setEditing(null);
        form.resetFields();
        await fetchList();
        void fetchCatTags();
      }
    } catch (err: any) {
      if (!err?.errorFields) getMsg()?.error("Error al guardar el costo");
    } finally {
      setSubmitting(false);
    }
  };

  const onDelete = async (id: number) => {
    const res = await costosSugeridosApi.remove(id);
    if (res) await fetchList();
  };

  const move = async (id: number, dir: -1 | 1) => {
    const idx = data.findIndex((c) => c.id === id);
    const j = idx + dir;
    if (idx < 0 || j < 0 || j >= data.length) return;
    const next = [...data];
    [next[idx], next[j]] = [next[j], next[idx]];
    setData(next);
    await costosSugeridosApi.reorder(next.map((c) => c.id));
  };

  const exportExcel = () => {
    void exportToXlsxSingleSheet({
      filename: "costos-sugeridos",
      sheetName: "Costos",
      rows: filtered.map((c) => ({
        Trabajo: c.nombre_trabajo,
        Categoría: c.categoria ?? "",
        "Precio ref. (CLP)": c.precio ?? "",
        Zona: zonaText(c),
        Descripción: c.descripcion ?? "",
        Fotos: Array.isArray(c.archivos) ? c.archivos.length : 0,
      })),
    });
  };

  const columns: ColumnsType<CostoSugerido> = [
    {
      title: "Trabajo",
      dataIndex: "nombre_trabajo",
      key: "nombre_trabajo",
      sorter: (a, b) => (a.nombre_trabajo ?? "").localeCompare(b.nombre_trabajo ?? ""),
      render: (v: string) => <b>{v}</b>,
    },
    {
      title: "Categoría",
      dataIndex: "categoria",
      key: "categoria",
      width: 170,
      sorter: (a, b) => (a.categoria ?? "").localeCompare(b.categoria ?? ""),
      render: (v: string) => (v ? <Tag color={tagColor(v, colorByCat.get(v))}>{v}</Tag> : "—"),
    },
    {
      title: "Precio ref.",
      dataIndex: "precio",
      key: "precio",
      width: 130,
      align: "right",
      sorter: (a, b) => (a.precio ?? 0) - (b.precio ?? 0),
      render: (v: number | null) => money(v),
    },
    {
      title: "Zona",
      key: "zona",
      width: 170,
      render: (_, c) => (
        <ZonaCell
          regionNombre={c.regionNombre}
          comunaNombre={c.comunaNombre}
          sectorNombre={c.sectorNombre}
          maxWidth={135}
        />
      ),
    },
    {
      title: "Descripción",
      dataIndex: "descripcion",
      key: "descripcion",
      ellipsis: true,
      render: (v: string) =>
        v ? (
          <Tooltip title={v}>
            <Text ellipsis style={{ maxWidth: 280 }}>
              {v}
            </Text>
          </Tooltip>
        ) : (
          "—"
        ),
    },
    {
      title: "Fotos",
      key: "archivos",
      width: 80,
      align: "center",
      render: (_, c) => {
        const n = Array.isArray(c.archivos) ? c.archivos.length : 0;
        return n > 0 ? (
          <Tag icon={<PictureOutlined />} color="geekblue">
            {n}
          </Tag>
        ) : (
          <Text type="secondary">—</Text>
        );
      },
    },
    {
      title: "Orden",
      key: "orden",
      width: 76,
      align: "center",
      render: (_, c) => {
        const idx = data.findIndex((x) => x.id === c.id);
        return (
          <Space size={0} onClick={stopClick}>
            <Tooltip title={canReorder ? "Subir" : "Quita búsqueda/filtros para reordenar"}>
              <Button
                type="text"
                size="small"
                icon={<ArrowUpOutlined />}
                disabled={!canReorder || idx <= 0}
                onClick={() => move(c.id, -1)}
              />
            </Tooltip>
            <Tooltip title="Bajar">
              <Button
                type="text"
                size="small"
                icon={<ArrowDownOutlined />}
                disabled={!canReorder || idx >= data.length - 1}
                onClick={() => move(c.id, 1)}
              />
            </Tooltip>
          </Space>
        );
      },
    },
    {
      title: "Acciones",
      key: "acciones",
      fixed: "right",
      width: 120,
      render: (_, c) => (
        <Space onClick={stopClick}>
          <Tooltip title="Editar">
            <Button icon={<EditOutlined />} onClick={() => openEdit(c)} />
          </Tooltip>
          <Popconfirm
            title="Eliminar costo"
            description="¿Seguro? Se borrarán también sus fotos."
            okText="Eliminar"
            cancelText="Cancelar"
            okButtonProps={{ danger: true }}
            onConfirm={() => onDelete(c.id)}
          >
            <Button danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <Layout
      sidebarOpen={sidebarOpen}
      toggleSidebar={toggleSidebar}
      containerClass="home-container"
      contentClass="home-content"
    >
      <div className="finanzas-page">
        <PageHeader
          title="Costos sugeridos"
          helpText="Catálogo de referencia de cuánto sale cada trabajo o reparación. Con precio referencial, categoría, zona opcional, descripción y fotos de guía. Usa el engranaje del selector de categoría para crear/editar/ordenar las categorías. Es solo referencia interna, no afecta finanzas."
        />

        <Space style={{ marginBottom: 16, width: "100%", flexWrap: "wrap" }}>
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
            Nuevo costo
          </Button>
          <Input
            allowClear
            prefix={<SearchOutlined style={{ color: "#bfbfbf" }} />}
            placeholder="Buscar por trabajo, categoría, zona o descripción…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ width: 320 }}
          />
          <Select
            mode="multiple"
            allowClear
            placeholder="Filtrar por categoría"
            style={{ minWidth: 220 }}
            value={filterCats}
            onChange={setFilterCats}
            options={catTags.map((t) => ({ value: t.nombre, label: t.nombre }))}
            maxTagCount="responsive"
          />
          <Button icon={<FileExcelOutlined />} onClick={exportExcel} disabled={!filtered.length}>
            Exportar Excel
          </Button>
        </Space>

        <Table<CostoSugerido>
          rowKey="id"
          loading={loading}
          dataSource={filtered}
          columns={columns}
          pagination={paginacionCliente({ porDefecto: 20, etiqueta: "costo" })}
          scroll={{ x: 1120 }}
          expandable={{
            expandRowByClick: true,
            showExpandColumn: false,
            expandedRowRender: (c) => (
              <div style={{ padding: "4px 8px" }}>
                {zonaText(c) && (
                  <Paragraph style={{ marginBottom: 8 }}>
                    <Text strong>Zona: </Text>
                    <EnvironmentOutlined style={{ color: "#0369a1", margin: "0 4px" }} />
                    {zonaText(c)}
                  </Paragraph>
                )}
                <Paragraph style={{ marginBottom: 0, whiteSpace: "pre-wrap" }}>
                  <Text strong>Descripción: </Text>
                  {c.descripcion || <Text type="secondary">—</Text>}
                </Paragraph>
              </div>
            ),
          }}
        />

        <Modal
          title={editing ? "Editar costo" : "Nuevo costo"}
          open={modalOpen}
          onCancel={closeModal}
          width={720}
          destroyOnClose
          footer={[
            <Button key="close" onClick={closeModal} disabled={submitting}>
              Cancelar
            </Button>,
            <Button key="save" type="primary" loading={submitting} onClick={submit}>
              {editing ? "Guardar cambios" : "Crear"}
            </Button>,
          ]}
        >
          <Form form={form} layout="vertical">
            <Form.Item
              name="nombre_trabajo"
              label="Nombre del trabajo"
              rules={[
                { required: true, message: "Ingresa el nombre del trabajo" },
                { min: 2, message: "Mínimo 2 caracteres" },
              ]}
            >
              <Input placeholder="Ej. Cambio de chapa, Destrabar puerta…" />
            </Form.Item>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 220px", gap: "0 16px" }}>
              <Form.Item label="Categoría">
                <CatalogSelect
                  tipo="categoria_costo"
                  value={categoriaVal}
                  onChange={(v) => setCategoriaVal((v as string) ?? null)}
                  placeholder="Elige una categoría"
                  manageTitle="Gestionar categorías"
                  onCatalogChanged={fetchCatTags}
                />
              </Form.Item>

              <Form.Item name="precio" label="Precio referencial (CLP)">
                <InputNumber
                  style={{ width: "100%" }}
                  min={0}
                  step={1000}
                  controls={false}
                  formatter={clpInputFormatter}
                  parser={clpInputParser}
                  placeholder="Ej. 25.000"
                />
              </Form.Item>
            </div>

            <Form.Item label="Zona (región / comuna / sector) — opcional">
              <GeoSelect value={geoVal} onChange={setGeoVal} />
            </Form.Item>

            <Form.Item
              name="descripcion"
              label="Descripción"
              tooltip="Detalle del trabajo: qué incluye, materiales, variantes de precio, recomendaciones."
            >
              <Input.TextArea
                rows={3}
                placeholder="¿Qué incluye este trabajo? ¿De qué depende el precio?"
              />
            </Form.Item>
          </Form>

          <div style={{ marginTop: 4 }}>
            <Text strong style={{ display: "block", marginBottom: 8 }}>
              Fotos de guía
            </Text>
            <AttachmentsManager
              apiBase="/costos-sugeridos"
              entityId={editing?.id ?? null}
              active={modalOpen}
              emptyHint="Los adjuntos se habilitan después de crear el costo: guárdalo y ábrelo con Editar."
            />
          </div>
        </Modal>
      </div>
    </Layout>
  );
}
